import { Download, Lock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '../list/expense-labels';
import { monthLabel } from '../list/list-query';
import { formatAmount } from '../money';
import { CloseDialog, ReopenDialog } from './CloseDialogs';
import { formatDay, formatKm } from './format';
import { IssuePanel } from './IssueRows';
import type { Month } from './types';
import { useMonthActions } from './use-month-actions';
import { downloadMonth, type DownloadKind } from './api';

function Inside({ month }: { readonly month: Month }) {
  const { t } = useTranslation('closing');
  const s = month.summary;
  const mileage = s.mileage;
  return (
    <section
      aria-label={t('inside.title')}
      className="border-border bg-card flex flex-col gap-1 rounded-lg border p-4 text-sm"
    >
      <h3 className="font-semibold">{t('inside.title')}</h3>
      <p className="flex flex-wrap justify-between gap-x-6">
        <span>
          {t('inside.counts', {
            approved: s.approvedCount ?? 0,
            reimbursed: s.reimbursedCount ?? 0,
            refused: s.refusedCount ?? 0,
          })}
        </span>
        <span>
          {t('inside.total')} <b className="tabular-nums">{formatAmount(s.total ?? '0.00')}</b>
        </span>
      </p>
      <p>
        {t('inside.toPay', {
          reimbursable: formatAmount(s.reimbursable ?? '0.00'),
          paid: formatAmount(s.paid ?? '0.00'),
          left: formatAmount(s.toPay ?? '0.00'),
        })}
      </p>
      {mileage !== undefined && (mileage.count ?? 0) > 0 ? (
        <p data-testid="inside-mileage">
          {t('inside.mileage', {
            count: mileage.count ?? 0,
            km: formatKm(mileage.km),
            amount: formatAmount(mileage.amount ?? '0.00'),
          })}
        </p>
      ) : null}
    </section>
  );
}

function StateBadge({ month }: { readonly month: Month }) {
  const { t } = useTranslation('closing');
  if (month.status === 'open') return <Badge tone="neutral">{t('status.open')}</Badge>;
  const date = month.closedAt ? formatDay(month.closedAt) : '';
  const name = month.closedBy?.displayName;
  return (
    <Badge tone="success">
      {name === undefined ? t('status.closedNoName', { date }) : t('status.closed', { date, name })}
    </Badge>
  );
}

function Downloads({ month, title }: { readonly month: string; readonly title: string }) {
  const { t } = useTranslation('closing');
  const [busy, setBusy] = useState<DownloadKind | null>(null);
  const [failed, setFailed] = useState(false);
  const run = async (kind: DownloadKind) => {
    setBusy(kind);
    setFailed(false);
    const done = await downloadMonth(month, kind);
    setBusy(null);
    setFailed(!done);
  };
  return (
    <section
      aria-label={title}
      className="border-border bg-card flex flex-col gap-2 rounded-lg border p-4"
    >
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {(['list', 'documents'] as const).map((kind) => (
          <Button
            key={kind}
            type="button"
            variant="outline"
            disabled={busy !== null}
            onClick={() => void run(kind)}
          >
            <Download />
            {t(`downloads.${kind}`)}
          </Button>
        ))}
      </div>
      {failed ? (
        <p role="alert" className="text-destructive text-sm">
          {t('downloads.failed')}
        </p>
      ) : null}
    </section>
  );
}

/** The buttons under a month and the dialogs they open: close, reopen, and what went wrong. */
function MonthActions({
  month,
  readOnly,
  adopt,
  reload,
}: {
  readonly month: Month;
  readonly readOnly: boolean;
  readonly adopt: (month: Month) => void;
  readonly reload: () => void;
}) {
  const { t, i18n } = useTranslation('closing');
  const [asking, setAsking] = useState<'close' | 'reopen' | null>(null);
  const actions = useMonthActions(month, adopt, reload, () => setAsking(null));
  const label = monthLabel(month.month, i18n.resolvedLanguage);
  const closed = month.status === 'closed';
  const blocked = month.blockers.length > 0;
  const stopAsking = () => {
    setAsking(null);
    actions.clearError();
  };

  return (
    <>
      {actions.error !== null && asking === null ? (
        <p role="alert" className="text-destructive text-sm">
          {(t as (key: string) => string)(`errors.${actions.error}`)}
        </p>
      ) : null}
      {month.can.close === true && !closed ? (
        <div className="flex flex-col items-end gap-1">
          <Button
            type="button"
            aria-disabled={blocked}
            className={blocked ? 'opacity-50' : undefined}
            onClick={blocked ? undefined : () => setAsking('close')}
          >
            <Lock />
            {t('close.button')}
          </Button>
          {blocked ? <span className="text-muted-foreground text-sm">{t('close.why')}</span> : null}
        </div>
      ) : null}
      {closed && !readOnly ? (
        <div className="flex flex-col items-end gap-1">
          {month.can.reopen === true ? (
            <Button type="button" variant="outline" onClick={() => setAsking('reopen')}>
              {t('after.reopen')}
            </Button>
          ) : (
            <span className="text-muted-foreground text-sm">{t('after.reopenHint')}</span>
          )}
          <span className="text-muted-foreground text-sm">{t('after.payHint')}</span>
        </div>
      ) : null}
      {asking === 'close' ? (
        <CloseDialog
          month={month}
          label={label}
          busy={actions.busy}
          onConfirm={() => void actions.close().then(() => setAsking(null))}
          onClose={stopAsking}
        />
      ) : null}
      {asking === 'reopen' ? (
        <ReopenDialog
          label={label}
          busy={actions.busy}
          error={actions.error}
          onConfirm={(reason) => void actions.reopen(reason)}
          onClose={stopAsking}
        />
      ) : null}
    </>
  );
}

/** One month (spec §1.6, §1.7, §1.8): what is in, what blocks, the warnings, and what closing does. */
export function MonthView({
  month,
  readOnly,
  adopt,
  reload,
}: {
  readonly month: Month;
  readonly readOnly: boolean;
  readonly adopt: (month: Month) => void;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('closing');
  const closed = month.status === 'closed';

  return (
    <div className="flex flex-col gap-4" data-testid="month-view">
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">{t('state')}:</span>
        <StateBadge month={month} />
      </p>
      {!closed && month.reopenedAt ? (
        <p className="text-muted-foreground text-sm">
          {t('reopened', {
            date: formatDay(month.reopenedAt),
            name: month.reopenedBy?.displayName ?? '',
            reason: month.reopenReason ?? '',
          })}
        </p>
      ) : null}
      <Inside month={month} />
      {readOnly ? null : (
        <>
          <IssuePanel
            title={t('blocks.title')}
            issues={month.blockers}
            kind="block"
            month={month.month}
            {...(closed ? {} : { clear: t('blocks.none') })}
          />
          <IssuePanel
            title={t('warns.title')}
            issues={month.warnings}
            kind="warn"
            month={month.month}
          />
        </>
      )}
      {month.can.export === true && (closed || readOnly) ? (
        <Downloads month={month.month} title={closed ? t('after.title') : t('downloads.title')} />
      ) : null}
      <MonthActions month={month} readOnly={readOnly} adopt={adopt} reload={reload} />
    </div>
  );
}
